import {
  CustomRatingApiModel,
  CustomSoloReportApiModel,
  SoloReportApiModel,
} from "../../api-models";
import personalIndicators from "./personal-indicators";
import teamIndicators from "./team-indicators";
import organisationIndicators from "./organisation-indicators";

type TeamIndicators = typeof teamIndicators;
type OrganisationIndicators = typeof organisationIndicators;

export const indicatorFactors = {
  PERSONAL: "Personal Indicator",
  TEAM: "Team Indicator",
  ORG: "Organisational Indicator",
} as const;

export type IndicatorFactor = (typeof indicatorFactors)[keyof typeof indicatorFactors];

export type IndicatorVariable = {
  name: string;
  z_score?: number;
  raw_rating: string;
};

export type TeamAndOrgIndicatorVariable = {
  z_score?: number;
  raw_rating: string;
  name: string;
  definition: string;
  long_definition: string;
  positive_indicators: string[];
  negative_indicators: string[];
  as_strength: string;
  as_gap: string;
};

export type FormattedCustomStrengthReturnType = TeamAndOrgIndicatorVariable[] | "no custom ratings";

const sortCustomValuesByRating = (
  values: TeamAndOrgIndicatorVariable[],
): TeamAndOrgIndicatorVariable[] => {
  return values.sort((a, b) => {
    const aRating = parseFloat(a.raw_rating.replace("%", ""));
    const bRating = parseFloat(b.raw_rating.replace("%", ""));
    return bRating - aRating;
  });
};

function getIndicatorDefinitionSource(type: "PERSONAL" | "TEAM" | "ORG") {
  switch (type) {
    case "PERSONAL":
      return personalIndicators;
    case "TEAM":
      return teamIndicators;
    default:
      return organisationIndicators;
  }
}

const getIndicator = (indicatorName: string, type: "PERSONAL" | "TEAM" | "ORG") => {
  const indicatorDefinitions = getIndicatorDefinitionSource(type);

  return {
    name: indicatorName,
    definition: indicatorDefinitions[indicatorName]?.shortDefinition ?? "not found",
    long_definition: indicatorDefinitions[indicatorName]?.longDefinition ?? "not found",
    positive_indicators: indicatorDefinitions[indicatorName]?.positiveIndicators ?? "not found",
    negative_indicators: indicatorDefinitions[indicatorName]?.negativeIndicators ?? "not found",
    as_strength: indicatorDefinitions[indicatorName]?.asStrength ?? "not found",
    as_gap: indicatorDefinitions[indicatorName]?.asGap ?? "not found",
  };
};

const formatIndicatorRatings = (
  report: CustomSoloReportApiModel,
  indicators: CustomRatingApiModel[],
  type: "TEAM" | "ORG",
) => {
  return indicators.map((r) => {
    const raw_rating = `${Math.trunc(r.rawRating!)}%`;
    const name = r.metadata.title;

    return {
      z_score: r.zScore,
      raw_rating,
      ...getIndicator(name, type),
    };
  });
};

export const formatIndicatorsFromCustomReport = (
  customReport: CustomSoloReportApiModel | undefined | null,
  type: "TEAM" | "ORG",
): FormattedCustomStrengthReturnType => {
  if (!customReport) {
    return "no custom ratings";
  }

  const indicators = customReport.userValues.ratings.filter((r) => {
    return type === "TEAM" ? r.metadata.uid.includes("team") : !r.metadata.uid.includes("team");
  });

  const values = formatIndicatorRatings(customReport, indicators, type);
  return values.length > 0 ? sortCustomValuesByRating(values) : "no custom ratings";
};

export const formatIndicators = (
  report: SoloReportApiModel,
  type: IndicatorFactor,
): IndicatorVariable[] => {
  const indicators = report.indicatorRatings.filter((r) => {
    return r.strength.factorName === type;
  });

  return indicators.map((r) => ({
    z_score: r.zScore,
    raw_rating: `${Math.trunc(r.rawRating!)}%`,
    ...getIndicator(
      r.strength.name,
      // eslint-disable-next-line no-nested-ternary
      type === indicatorFactors.PERSONAL
        ? "PERSONAL"
        : type === indicatorFactors.TEAM
        ? "TEAM"
        : "ORG",
    ),
  }));
};
